import { languageInfo } from '../i18n/languages'
import type { Language } from '../types/document'
import { verbalize } from './verbalize'

// Text-to-speech using only the phone/browser's built-in voices (Web Speech API).
// No audio is generated on a server and nothing is sent anywhere.

export type SpeechState = 'idle' | 'loading' | 'speaking' | 'paused'

export function speechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window
}

/** Voices load asynchronously on Chrome/Android; wait briefly for them. */
export function loadVoices(timeoutMs = 1500): Promise<SpeechSynthesisVoice[]> {
  if (!speechSupported()) return Promise.resolve([])
  const synth = window.speechSynthesis
  const now = synth.getVoices()
  if (now.length > 0) return Promise.resolve(now)
  return new Promise((resolve) => {
    const done = () => {
      synth.removeEventListener('voiceschanged', done)
      resolve(synth.getVoices())
    }
    synth.addEventListener('voiceschanged', done)
    setTimeout(done, timeoutMs)
  })
}

/** Best voice for a BCP-47 tag like "ta-IN": exact match first, then same language. */
export function findVoice(voices: SpeechSynthesisVoice[], bcp47: string): SpeechSynthesisVoice | null {
  const norm = (tag: string) => tag.replace('_', '-').toLowerCase()
  const wanted = norm(bcp47)
  const base = wanted.split('-')[0]
  return (
    voices.find((v) => norm(v.lang) === wanted && v.localService) ??
    voices.find((v) => norm(v.lang) === wanted) ??
    voices.find((v) => norm(v.lang).split('-')[0] === base) ??
    null
  )
}

/**
 * Chunks for the built-in voice: it synthesises roughly in real time on a
 * phone, so the first chunk is kept short (audio starts sooner) and the rest
 * are synthesised while earlier ones play.
 */
export function toBuiltInChunks(sentences: string[]): string[] {
  const chunks = toSpeechChunks(sentences, 110)
  if (chunks.length > 0 && chunks[0].length > 60) {
    const first = chunks[0]
    const cut = Math.max(first.lastIndexOf(',', 60), first.lastIndexOf(' ', 60))
    if (cut > 20) chunks.splice(0, 1, first.slice(0, cut + 1).trim(), first.slice(cut + 1).trim())
  }
  return chunks
}

/** Removes emoji/symbols that voices read out literally, and splits into short chunks. */
export function toSpeechChunks(sentences: string[], maxLength = 180): string[] {
  const chunks: string[] = []
  for (const raw of sentences) {
    const text = raw
      .replace(/[\p{Extended_Pictographic}️‍]/gu, '')
      .replace(/[—–]/g, ', ')
      .replace(/\s+/g, ' ')
      .trim()
    if (!text) continue
    if (text.length <= maxLength) {
      chunks.push(text)
      continue
    }
    // Long sentences stop mid-way on some Android versions; split at commas/spaces
    let rest = text
    while (rest.length > maxLength) {
      const window_ = rest.slice(0, maxLength)
      const cut = Math.max(window_.lastIndexOf(','), window_.lastIndexOf('،'), window_.lastIndexOf(' '))
      const at = cut > maxLength / 2 ? cut + 1 : maxLength
      chunks.push(rest.slice(0, at).trim())
      rest = rest.slice(at).trim()
    }
    if (rest) chunks.push(rest)
  }
  return chunks
}

/**
 * Plays a list of chunks one by one. Pause is implemented as "stop and remember
 * the current chunk" because speechSynthesis.pause() is unreliable on Android.
 */
export class Speaker {
  private chunks: string[] = []
  private index = 0
  private generation = 0
  private lang = 'hi-IN'
  private voice: SpeechSynthesisVoice | null = null
  private state: SpeechState = 'idle'

  constructor(private onState: (state: SpeechState) => void) {}

  play(chunks: string[], lang: string, voice: SpeechSynthesisVoice | null): void {
    if (!speechSupported() || chunks.length === 0) return
    this.chunks = chunks
    this.index = 0
    this.lang = lang
    this.voice = voice
    this.speakCurrent()
  }

  pause(): void {
    if (this.state !== 'speaking') return
    this.generation += 1
    window.speechSynthesis.cancel()
    this.setState('paused')
  }

  resume(): void {
    if (this.state !== 'paused') return
    this.speakCurrent()
  }

  stop(): void {
    this.generation += 1
    this.chunks = []
    this.index = 0
    if (speechSupported()) window.speechSynthesis.cancel()
    this.setState('idle')
  }

  private speakCurrent(): void {
    const synth = window.speechSynthesis
    this.generation += 1
    const generation = this.generation
    synth.cancel()
    const next = () => {
      if (generation !== this.generation) return
      if (this.index >= this.chunks.length) {
        this.stop()
        return
      }
      const utterance = new SpeechSynthesisUtterance(this.chunks[this.index])
      utterance.lang = this.lang
      if (this.voice) utterance.voice = this.voice
      utterance.rate = 0.9
      utterance.onend = () => {
        if (generation !== this.generation) return
        this.index += 1
        next()
      }
      utterance.onerror = () => {
        if (generation !== this.generation) return
        this.stop()
      }
      synth.speak(utterance)
    }
    this.setState('speaking')
    next()
  }

  private setState(state: SpeechState) {
    if (this.state === state) return
    this.state = state
    this.onState(state)
  }
}

// ── Built-in neural voices (Meta MMS) for languages the device cannot speak ──
//
// Many phones and most Windows PCs only have English/Hindi text-to-speech
// voices. For the other languages Samjho uses Meta's open MMS-TTS voices
// (VITS, CC-BY-NC-4.0), converted to ONNX and served from /voices/<lang>/.
// They run entirely on the device with ONNX Runtime Web (via Transformers.js):
// each language's voice (~58 MB, half precision: int8 was 2x slower on
// phones) is downloaded once, cached, and then works
// offline. Numbers are spelled out as words first, because these voices can't
// read digits.

type Tts = { tokenizer: (text: string) => unknown; model: (inputs: unknown) => Promise<{ waveform: { data: Float32Array } }>; rate: number }
const voiceCache = new Map<string, Promise<Tts>>()
const VOICE_BASE = `${import.meta.env.BASE_URL}voices/`

export function builtInVoiceSupported(): boolean {
  return typeof WebAssembly !== 'undefined' && typeof Audio !== 'undefined'
}

/**
 * Transformers.js reads model files through this cache hook; the model file is
 * stored in parts (Cloudflare limits files to 25 MB), so it is reassembled here
 * and kept in Cache Storage for offline use.
 */
const partsCache = {
  async match(key: string): Promise<Response | undefined> {
    const url = typeof key === 'string' ? key : String(key)
    const store = 'caches' in window ? await caches.open('samjho-voices-v2') : null
    const hit = await store?.match(url)
    if (hit) return hit
    if (!url.endsWith('.onnx')) return undefined
    const manifest = await fetch(`${url}.parts.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null) as { parts: string[] } | null
    if (!manifest) return undefined
    const base = url.slice(0, url.lastIndexOf('/') + 1)
    const blobs = await Promise.all(manifest.parts.map((p) => fetch(base + p).then((r) => {
      if (!r.ok) throw new Error('voice-download')
      return r.blob()
    })))
    const response = new Response(new Blob(blobs), { headers: { 'content-type': 'application/octet-stream' } })
    await store?.put(url, response.clone())
    return response
  },
  async put(key: string, response: Response) {
    if (!('caches' in window)) return
    const store = await caches.open('samjho-voices-v2')
    await store.put(typeof key === 'string' ? key : String(key), response)
  }
}

function loadVoice(voice: string): Promise<Tts> {
  let job = voiceCache.get(voice)
  if (!job) {
    job = (async () => {
      const { env, AutoTokenizer, VitsModel } = await import('@huggingface/transformers')
      env.allowRemoteModels = false
      env.allowLocalModels = true
      env.localModelPath = VOICE_BASE
      env.useBrowserCache = false
      env.useCustomCache = true
      env.customCache = partsCache
      const wasm = env.backends.onnx.wasm
      if (wasm) {
        wasm.wasmPaths = `${import.meta.env.BASE_URL}ort/`
        // Several CPU threads when the page is cross-origin isolated (see public/_headers)
        wasm.numThreads = self.crossOriginIsolated ? Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1)) : 1
        // Run inference in a background worker so the page never freezes while speaking
        wasm.proxy = true
      }
      const [tokenizer, model] = await Promise.all([
        AutoTokenizer.from_pretrained(voice),
        VitsModel.from_pretrained(voice, { dtype: 'fp16', device: 'wasm' })
      ])
      return {
        tokenizer: (text: string) => tokenizer(text),
        model: (inputs: unknown) => (model as unknown as (i: unknown) => Promise<{ waveform: { data: Float32Array } }>)(inputs),
        rate: (model.config as unknown as { sampling_rate: number }).sampling_rate
      }
    })()
    job.catch(() => voiceCache.delete(voice)) // allow a retry (e.g. after coming back online)
    voiceCache.set(voice, job)
  }
  return job
}

function toWav(samples: Float32Array, rate: number): Blob {
  const data = new DataView(new ArrayBuffer(44 + samples.length * 2))
  const text = (offset: number, s: string) => [...s].forEach((c, i) => data.setUint8(offset + i, c.charCodeAt(0)))
  text(0, 'RIFF'); data.setUint32(4, 36 + samples.length * 2, true); text(8, 'WAVE'); text(12, 'fmt ')
  data.setUint32(16, 16, true); data.setUint16(20, 1, true); data.setUint16(22, 1, true)
  data.setUint32(24, rate, true); data.setUint32(28, rate * 2, true); data.setUint16(32, 2, true); data.setUint16(34, 16, true)
  text(36, 'data'); data.setUint32(40, samples.length * 2, true)
  samples.forEach((v, i) => data.setInt16(44 + i * 2, Math.max(-1, Math.min(1, v)) * 32767, true))
  return new Blob([data], { type: 'audio/wav' })
}

// Recently synthesised chunks, so a chunk prepared in the background (or
// replayed) plays instantly. Kept small: it holds audio of the current document only.
const synthMemo = new Map<string, Promise<Blob>>()

/** Synthesises one chunk of (already verbalised) text to a WAV blob, on the device. */
export function synthesizeBuiltIn(text: string, voice: string): Promise<Blob> {
  const key = `${voice}|${text}`
  let job = synthMemo.get(key)
  if (!job) {
    job = (async () => {
      const tts = await loadVoice(voice)
      const { waveform } = await tts.model(tts.tokenizer(text))
      return toWav(waveform.data, tts.rate)
    })()
    job.catch(() => synthMemo.delete(key))
    synthMemo.set(key, job)
    while (synthMemo.size > 12) synthMemo.delete(synthMemo.keys().next().value as string)
  }
  return job
}

/**
 * The whole explanation as one audio file, made on the phone with the
 * built-in voice, so it can be sent as a voice note (e.g. to a parent on
 * WhatsApp). Nothing is uploaded: the phone's share sheet sends it.
 */
export async function renderVoiceNote(sentences: string[], language: Language): Promise<File> {
  const voice = languageInfo(language).voice
  if (!voice || !builtInVoiceSupported()) throw new Error('no-voice')
  const tts = await loadVoice(voice)
  const gap = new Float32Array(Math.round(tts.rate * 0.35))
  const parts: Float32Array[] = []
  for (const chunk of toBuiltInChunks(sentences)) {
    const { waveform } = await tts.model(tts.tokenizer(verbalize(chunk, language)))
    parts.push(waveform.data, gap)
  }
  const all = new Float32Array(parts.reduce((n, part) => n + part.length, 0))
  let offset = 0
  for (const part of parts) {
    all.set(part, offset)
    offset += part.length
  }
  return new File([toWav(all, tts.rate)], `samjho-${language}.wav`, { type: 'audio/wav' })
}

export function clearSpeechMemo(): void {
  synthMemo.clear()
}

/** Is this language's voice already on the device? (Then it can be warmed up without using data.) */
export async function isVoiceDownloaded(voice: string): Promise<boolean> {
  if (!('caches' in window)) return false
  const store = await caches.open('samjho-voices-v2')
  return Boolean(await store.match(new URL(`${VOICE_BASE}${voice}/onnx/model_fp16.onnx`, location.href).href))
}

/**
 * Warm-up: if the voice is already downloaded, load it and synthesise the first
 * chunks in the background so tapping Sunao starts speaking immediately.
 */
export async function prepareBuiltIn(sentences: string[], language: Language): Promise<void> {
  const voice = languageInfo(language).voice
  if (!voice || !builtInVoiceSupported() || !(await isVoiceDownloaded(voice))) return
  // The first few sentences, one after another: playback then starts at once and
  // stays ahead of the voice even on slower phones
  for (const chunk of toBuiltInChunks(sentences).slice(0, 3)) {
    await synthesizeBuiltIn(verbalize(chunk, language), voice).catch(() => undefined)
  }
}

/**
 * Same controls as Speaker (play / pause / resume / stop), backed by the
 * built-in neural voice. The next chunk is synthesised while the current one plays.
 */
export class BuiltInSpeaker {
  private chunks: string[] = []
  private index = 0
  private generation = 0
  private language = 'hi'
  private audio: HTMLAudioElement | null = null
  private url = ''
  private pending = new Map<number, Promise<Blob>>()
  private state: SpeechState = 'idle'

  constructor(private onState: (state: SpeechState) => void) {}

  play(chunks: string[], language: string): void {
    this.stop()
    if (chunks.length === 0) return
    this.chunks = chunks
    this.language = language
    this.index = 0
    this.setState('loading')
    void this.playCurrent(this.generation)
  }

  pause(): void {
    if (this.state !== 'speaking' || !this.audio) return
    this.audio.pause()
    this.setState('paused')
  }

  resume(): void {
    if (this.state !== 'paused' || !this.audio) return
    void this.audio.play()
    this.setState('speaking')
  }

  stop(): void {
    this.generation += 1
    this.pending.clear()
    if (this.audio) {
      this.audio.pause()
      this.audio.src = ''
      this.audio = null
    }
    if (this.url) URL.revokeObjectURL(this.url)
    this.url = ''
    this.chunks = []
    this.setState('idle')
  }

  private synth(i: number): Promise<Blob> {
    let job = this.pending.get(i)
    if (!job) {
      const lang = this.language as Language
      const voice = languageInfo(lang).voice
      job = voice ? synthesizeBuiltIn(verbalize(this.chunks[i], lang), voice) : Promise.reject(new Error('no-voice'))
      this.pending.set(i, job)
    }
    return job
  }

  private async playCurrent(generation: number): Promise<void> {
    if (generation !== this.generation) return
    if (this.index >= this.chunks.length) {
      this.stop()
      return
    }
    try {
      const blob = await this.synth(this.index)
      if (generation !== this.generation) return
      if (this.index + 1 < this.chunks.length) void this.synth(this.index + 1).catch(() => undefined)
      if (this.url) URL.revokeObjectURL(this.url)
      this.url = URL.createObjectURL(blob)
      const audio = new Audio(this.url)
      this.audio = audio
      audio.onended = () => {
        if (generation !== this.generation) return
        this.pending.delete(this.index)
        this.index += 1
        void this.playCurrent(generation)
      }
      audio.onerror = () => generation === this.generation && this.stop()
      await audio.play()
      if (generation === this.generation && this.state !== 'paused') this.setState('speaking')
    } catch {
      if (generation === this.generation) this.stop()
    }
  }

  private setState(state: SpeechState) {
    if (this.state === state) return
    this.state = state
    this.onState(state)
  }
}
